/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Shown when the rules refuse a PT programme. Names neither the programme nor
 * its coach: the visitor had the link, not the right to know what it is.
 */

import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, PrivateIcon } from "@lemonadetraining/web-ui";
import {
  loadUserSubInfoForProgram,
  trackAccessEvent,
  useAppSelector,
} from "@lemonadetraining/web-shared";
import { ACCESS_EVENTS } from "@lemonadetraining/contracts";
import MyCard from "../components/Cards/MyCard";
import { useUserAccess } from "../hooks/useUserAccess";
import {
  accessEventViewerRole,
  isExpectedProgrammeOrigin,
  privateProgrammeBackTarget,
} from "../Common/privateProgramme";

interface Props {
  programmeID: string;
}

const PrivateProgrammePage: React.FC<Props> = ({ programmeID }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const access = useUserAccess();
  const uid = useAppSelector((state) => state.user.user?.uid ?? null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const emitted = useRef(false);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    // On a hard load the user lands after the loader ran. The ref stops
    // StrictMode's double mount counting one visit twice.
    if (access.isLoading || !uid || emitted.current) return;
    emitted.current = true;
    const fromOwnList = isExpectedProgrammeOrigin(location.state);
    const inLibrary = fromOwnList
      ? Promise.resolve(true)
      : loadUserSubInfoForProgram(uid, programmeID).then(Boolean, () => false);
    void inLibrary.then((expectedAccess) =>
      trackAccessEvent(ACCESS_EVENTS.privateProgrammeDeniedView, {
        platform: "web",
        viewerRole: accessEventViewerRole(access),
        expectedAccess,
      })
    );
  }, [access, uid, programmeID, location.state]);

  return (
    <MyCard>
      <div className="flex flex-col items-start gap-3" data-testid="private-programme-page">
        <PrivateIcon className="size-8 text-muted-foreground" aria-hidden="true" />
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-xl font-semibold text-foreground focus:outline-none"
        >
          This programme is private
        </h1>
        <p className="text-sm text-muted-foreground">
          PT programmes are only visible to the coach who created them, the athlete and the
          gym&apos;s admins.
        </p>
        <Button className="min-h-11" onClick={() => navigate(privateProgrammeBackTarget(access))}>
          Back to programming
        </Button>
      </div>
    </MyCard>
  );
};

export default PrivateProgrammePage;
